import { plain } from '../../lib/labels';
import {
  Card,
  Input,
  Table,
  Tag,
  Typography,
  Space,
  type TableColumnsType,
} from 'antd';
import { useState } from 'react';
import { api } from '../../api/client';
import { QueryError } from '../../lib/QueryState';
import { PersonName } from '../../lib/listenerName';
import { LoadMore, useCursorPages } from '../../lib/useCursorPages';

interface Party {
  userId: string;
  handle: string;
  avatarSeed: string | null;
  /** Listener display name when this party is a listener. */
  displayName?: string | null;
  /** The account's own user Display Name — absent on older APIs. */
  userDisplayName?: string | null;
}

interface BlockRow {
  id: string;
  blocker: Party;
  blocked: Party;
  reason: string | null;
  source: string | null;
  createdAt: string;
}

interface Page {
  items: BlockRow[];
  nextCursor: string | null;
}

function PartyCell({ p }: { p: Party }) {
  return (
    <PersonName displayName={p.displayName} userDisplayName={p.userDisplayName} handle={p.handle} userId={p.userId} />
  );
}

const dash = <span style={{ color: '#8B8B8B' }}>—</span>;

export function BlocksPage() {
  const [q, setQ] = useState('');
  const list = useCursorPages<BlockRow>(['admin', 'blocks', q], async (cursor) =>
    (await api.get<Page>('/admin/blocks', { params: { limit: 50, cursor, q: q || undefined } })).data,
  );

  const columns: TableColumnsType<BlockRow> = [
    { title: 'Blocker', render: (_: unknown, r) => <PartyCell p={r.blocker} /> },
    { title: 'Blocked', render: (_: unknown, r) => <PartyCell p={r.blocked} /> },
    {
      title: 'Source',
      dataIndex: 'source',
      render: (s: string | null) => (s ? <Tag>{plain(s)}</Tag> : dash),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (s: string | null) => s ?? dash,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
    },
  ];

  return (
    <Card>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        User blocks
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Peer-to-peer blocks — who blocked whom. Read-only.
      </Typography.Paragraph>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          aria-label="Search blocks"
          placeholder="Either party: name or @handle"
          onSearch={(v) => setQ(v.trim())}
          style={{ minWidth: 300 }}
        />
      </Space>
      {list.isError && <QueryError error={list.error} what="user blocks" onRetry={list.refetch} style={{ marginBottom: 12 }} />}
      <Table<BlockRow>
        rowKey="id"
        loading={list.isLoading}
        columns={columns}
        dataSource={list.rows}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        locale={{ emptyText: q ? `No blocks involving “${q}”` : 'No blocks' }}
      />
      <LoadMore list={list} noun="blocks" />
    </Card>
  );
}
